// One trail for the top bar. A page whose path carries data (a client's
// name, a matter's eID) registers its steps with usePageTrail; every other
// route is named by the table below or by its module's trail rows. The
// root is the Command Center, the word the rail and the palette already
// use for "/".

import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { matchPath } from "react-router-dom";
import { useScreens, type ModuleTrailRow } from "./screens";

export interface TrailStep {
  label: string;
  to?: string;
  /** The label is still loading: the bar draws a skeleton in its place. */
  pending?: boolean;
}

export const ROOT: TrailStep = { label: "Command Center", to: "/" };
export const PENDING: TrailStep = { label: "", pending: true };

const SETTINGS: TrailStep = { label: "Settings" };

// Routes whose steps need no page data. The moved paths keep their
// target's name so a bookmark never flashes "Not found" on the way.
const ROUTE_TRAILS: Array<{ path: string; steps: TrailStep[] }> = [
  { path: "/", steps: [] },
  { path: "/cases", steps: [{ label: "Cases" }] },
  { path: "/clients", steps: [{ label: "Clients" }] },
  { path: "/calendar", steps: [{ label: "Calendar" }] },
  { path: "/filings", steps: [{ label: "Filings" }] },
  { path: "/inbox", steps: [{ label: "Inbox" }] },
  { path: "/team", steps: [{ label: "Team" }] },
  { path: "/profile", steps: [{ label: "Profile" }] },
  { path: "/settings/firm", steps: [SETTINGS, { label: "Firm profile" }] },
  { path: "/settings/modules", steps: [SETTINGS, { label: "Modules" }] },
  { path: "/settings/integrations", steps: [SETTINGS, { label: "Integrations" }] },
  { path: "/integrations", steps: [SETTINGS, { label: "Integrations" }] },
  { path: "/settings/forms", steps: [SETTINGS, { label: "Forms" }] },
  { path: "/settings/email", steps: [SETTINGS, { label: "Email log" }] },
];

export function routeTrail(pathname: string, moduleRows: ModuleTrailRow[] = []): TrailStep[] {
  const hit = [...ROUTE_TRAILS, ...moduleRows].find((r) =>
    matchPath({ path: r.path, end: true }, pathname),
  );
  return [ROOT, ...(hit ? hit.steps : [{ label: "Not found" }])];
}

/** The tab's title: the last step whose label is known, then the product. */
export function titleOf(steps: TrailStep[]): string {
  const known = steps.filter((s) => !s.pending);
  return (known[known.length - 1] ?? ROOT).label + " - DuraLex";
}

interface TrailSetter {
  register: (steps: TrailStep[]) => void;
  unregister: () => void;
}

// Two contexts: pages write through the setter and never re-render when
// the trail changes; only the bar reads the value.
const TrailSetterContext = createContext<TrailSetter | null>(null);
export const TrailValueContext = createContext<TrailStep[] | null>(null);

export function TrailProvider({ children }: { children: ReactNode }) {
  const [steps, setSteps] = useState<TrailStep[] | null>(null);
  // A leaving page's cleanup runs before the next page's effect, so a
  // plain clear is enough: the next registration lands after it.
  const setter = useMemo<TrailSetter>(
    () => ({ register: setSteps, unregister: () => setSteps(null) }),
    [],
  );
  return (
    <TrailSetterContext.Provider value={setter}>
      <TrailValueContext.Provider value={steps}>{children}</TrailValueContext.Provider>
    </TrailSetterContext.Provider>
  );
}

/** A record's step: its name, a skeleton while it loads, else the type noun. */
export function recordStep(
  name: string | null | undefined,
  loading: boolean,
  noun: string,
): TrailStep {
  return name ? { label: name } : loading ? PENDING : { label: noun };
}

// A layout effect, so the bar never paints the route table's guess first;
// without a provider it does nothing.
export function usePageTrail(steps: TrailStep[]) {
  const setter = useContext(TrailSetterContext);
  const key = JSON.stringify(steps);
  useLayoutEffect(() => {
    setter?.register([ROOT, ...(JSON.parse(key) as TrailStep[])]);
  }, [setter, key]);
  useLayoutEffect(() => () => setter?.unregister(), [setter]);
}

export function useTrail(pathname: string): TrailStep[] {
  const registered = useContext(TrailValueContext);
  const screens = useScreens();
  return useMemo(
    () =>
      registered ??
      routeTrail(
        pathname,
        screens.flatMap((s) => s.trails ?? []),
      ),
    [registered, pathname, screens],
  );
}
